import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import { LINK_LIST_ACTION_SUBJECT, LINK_LIST_ANALYTICS_CHANNEL } from './constants';
import { createLinkClickHandlers } from './create-link-click-handlers';
import { hasEventFired, markEventFired } from './fired-event-cache';
import type { OnLinkClick } from './types';

interface LinkListErroredFallbackProps {
	onLinkClick?: OnLinkClick;
	/**
	 * Identifies the surface/product the link list is rendered in (e.g.
	 * `'luminaSAIN'`). Included in the errored analytics event attributes.
	 */
	source?: string;
	/**
	 * Prefix used to namespace the fallback item testIds
	 */
	testIdPrefix: string;
	urls: string[];
}

/**
 * Fallback rendered when every URL passed into a link list variant has errored
 * out via the Smart Link Card. Rather than attempting another smart-card
 * resolution (which would also error and throw in the same environment), we
 * render a plain ADS `Link` for each URL so the user still has something
 * clickable.
 *
 * The wrapper `Box` carries `onAuxClick` / `onContextMenu` so middle-click and
 * right-click telemetry behavior matches the resolved (block) variant.
 */
export const LinkListErroredFallback = ({
	urls,
	onLinkClick,
	testIdPrefix,
	source,
}: LinkListErroredFallbackProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Fire the "errored" event exactly once per unique link set, i.e. when every
	// URL in the list has failed to resolve. Because this component is re-mounted
	// on every streamed chunk, a mount-scoped guard would fire duplicates, so we
	// dedupe against a page-lifetime, SSR-safe, bounded module cache keyed by the
	// (order-independent) set of URLs.
	useEffect(() => {
		const key = `${LINK_LIST_ACTION_SUBJECT}::errored::${[...urls].sort().join('|')}`;
		if (hasEventFired(key)) {
			return;
		}
		markEventFired(key);
		createAnalyticsEvent({
			eventType: 'track',
			action: 'errored',
			actionSubject: LINK_LIST_ACTION_SUBJECT,
			attributes: {
				urlCount: urls.length,
				source,
			},
		}).fire(LINK_LIST_ANALYTICS_CHANNEL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Stack space="space.100">
			{urls.map((url, idx) => {
				const { onClick, onAuxClick, onContextMenu } = createLinkClickHandlers(
					onLinkClick,
					url,
					idx,
				);
				return (
					<Box
						key={`${url}-${idx}-errored-fallback`}
						testId={`${testIdPrefix}-errored-fallback-item-${idx}`}
						onAuxClick={onAuxClick}
						onContextMenu={onContextMenu}
					>
						<Link href={url} onClick={onClick} target="_blank" rel="noopener noreferrer">
							{url}
						</Link>
					</Box>
				);
			})}
		</Stack>
	);
};
